<script lang="ts" setup>
import Icon from '@/components/shared/Icon.vue'
import { generateIconColor } from '@/lib'

interface Props {
  icon?: string
  variant?: string
  tag?: string
  disabled?: boolean
}

withDefaults(defineProps<Props>(), {
  icon: 'square-pen',
  variant: 'primary',
  tag: 'button', // Default tag is button
  disabled: false,
})
</script>

<template>
  <component :is="tag" type="button" :class="variant" class="button default-focus" :disabled="disabled">
    <slot>
      <Icon :variant="icon" class="w-4 h-4" :stroke-width="1.7" v-bind="generateIconColor('primary')" />
    </slot>
  </component>
</template>

<style scoped lang="postcss">
.button {
  @apply inline-flex h-6 w-6 shadow-sm items-center justify-center rounded-md border [&>*]:pointer-events-none focus-visible:outline-none;

  &.primary {
    @apply bg-gray-100 border-gray-300 text-primary-500 hover:shadow-sm active:bg-primary-100
           dark:bg-gray-800 dark:border-gray-600 dark:text-primary-400 dark:hover:bg-gray-700 dark:active:bg-gray-600;
  }

  &.white {
    @apply bg-white border-gray-300 text-primary-500 hover:shadow-sm active:bg-primary-100
           dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600;
  }

  &.error {
    @apply bg-error-50 border-error-200 text-error-600
           dark:bg-error-900/50 dark:border-error-600 dark:text-error-300 dark:hover:bg-error-800/50 dark:active:bg-error-950;
  }

  &.warning {
    @apply bg-yellow-50 border-yellow-200 text-yellow-800
           dark:bg-yellow-900/50 dark:border-yellow-600 dark:text-yellow-300 dark:hover:bg-yellow-800/50 dark:active:bg-yellow-950;
  }

  &.pending {
    @apply bg-orange-50 border-orange-200 text-orange-800
           dark:bg-orange-900/50 dark:border-orange-600 dark:text-orange-300 dark:hover:bg-orange-800/50 dark:active:bg-orange-950;
  }

  &.success {
    @apply bg-success-50 border-success-200 text-success-700
           dark:bg-success-900/50 dark:border-success-600 dark:text-success-300 dark:hover:bg-success-800/50 dark:active:bg-success-950;
  }
}
</style>
